import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, Subscription, forkJoin, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  catchError,
  map
} from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

import { BaseServices } from '../../../kuba.services';

import { AppraisalTemplateService } from '../templates/services';
import {
  AppraisalTemplateListItemDto,
  AppraisalTemplateType
} from '../templates/models';
import { AppraisalTemplatePaginatedFilterDto } from '../templates/models/appraisal-template-paginated-filter.dto';
import { AppraisalTemplateBatchStatusDto } from '../templates/models/appraisal-template-batch-status.dto';

type TemplateFamilyRow = {
  FamilyKey: string;
  DisplayName: string;
  CurrentTemplate: AppraisalTemplateListItemDto;
  Versions: AppraisalTemplateListItemDto[];
};

@Component({
  selector: 'appraisals-templates',
  templateUrl: './appraisals-templates.component.html',
  styleUrls: ['./appraisals-templates.component.scss'],
  standalone: false
})
export class AppraisalsTemplatesComponent implements OnInit, OnDestroy {
  //#region Variables
  private readonly notifications = inject(NotificationService);

  // Data
  allTemplateVersions: AppraisalTemplateListItemDto[] = [];
  templateFamilies: TemplateFamilyRow[] = [];
  selectedTemplates: TemplateFamilyRow[] = [];
  currentActionTemplate: AppraisalTemplateListItemDto | null = null;
  actionMenuPosition = { top: 0, left: 0 };

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  first: number = 0;

  // State variables
  isLoading: boolean = false;
  openChangeStatusDialog: boolean = false;
  isDeletingSelectedTemplates: boolean = false;
  isUpdatingSelectedTemplateStatuses: boolean = false;
  selectedTemplateStatusTarget: boolean | null = null;
  templateActionStates: Record<number, 'toggling' | 'deleting'> = {};

  // Filters
  nameFilter: string = '';
  typeFilter?: AppraisalTemplateType = null;
  statusFilter?: boolean = null;
  departmentFilter: string = '';

  // Filter change subject for debouncing
  private filterChangeSubject = new Subject<{
    name: string;
    type: AppraisalTemplateType | null;
    status: boolean | null;
    department: string;
  }>();

  // Sorting
  sortField: string = 'CreatedOn';
  sortOrder: 'ASC' | 'DESC' = 'DESC';

  // Permissions
  canCreateTemplate: boolean = true;
  canEditTemplate: boolean = true;
  canDeleteTemplate: boolean = true;
  canViewTemplates: boolean = true;

  // Template type options
  templateTypeOptions: any[] = [
    { label: this.translate.instant('ALL_TYPES'), value: null },
    {
      label: this.translate.instant('STANDARD'),
      value: AppraisalTemplateType.Standard
    },
    {
      label: this.translate.instant('LEADER'),
      value: AppraisalTemplateType.Leader
    },
    {
      label: this.translate.instant('PROJECT'),
      value: AppraisalTemplateType.Project
    }
  ];

  // Status options
  statusOptions: any[] = [
    { label: this.translate.instant('ALL_STATUSES'), value: null },
    { label: this.translate.instant('ACTIVE'), value: true },
    { label: this.translate.instant('INACTIVE'), value: false }
  ];

  private subscriptions: Subscription[] = [];
  //#endregion

  //#region Constructor
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private templateService: AppraisalTemplateService
  ) {
    // Setup filter debounce
    this.subscriptions.push(
      this.filterChangeSubject
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(() => {
          this.currentPage = 1;
          this.first = 0;
          this.loadTemplates();
        })
    );
  }
  //#endregion

  //#region Lifecycle Hooks
  ngOnInit(): void {
    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
  //#endregion

  //#region Data Loading
  /**
   * Load templates list with filters and pagination
   */
  loadTemplates(): void {
    this.isLoading = true;

    const filters: AppraisalTemplatePaginatedFilterDto = {
      pageNumber: 1,
      pageSize: 5000,
      sortColumn: 'CreatedOn',
      sortDirection: 'DESC'
    };

    this.subscriptions.push(
      this.templateService
        .getPagedList(BaseServices.BusinessId, filters)
        .subscribe({
          next: (response: any) => {
            this.allTemplateVersions = response.Items || [];
            this.applyTemplateFamilies();
            this.resetTemplateActionState();
            this.isLoading = false;
          },
          error: error => {
            console.error('Error loading forms:', error);
            this.resetTemplateActionState();
            this.isLoading = false;
          }
        })
    );
  }
  //#endregion

  //#region Event Handlers
  /**
   * Handle page change event
   */
  onPageChange(event: any): void {
    this.currentPage = Math.floor(event.first / event.rows) + 1;
    this.pageSize = event.rows;
    this.first = event.first;
    this.loadTemplates();
  }

  /**
   * Handle sort event
   */
  onSort(event: any): void {
    const sortMap: Record<string, string> = {
      displayName: 'Name',
      name: 'Name',
      Name: 'Name',
      createdOn: 'CreatedOn',
      CreatedOn: 'CreatedOn',
      'currentTemplate.createdBy.createdOn': 'CreatedOn',
      currentTemplate: 'CreatedOn',
      createdBy: 'CreatedOn',
      templateType: 'TemplateType',
      TemplateType: 'TemplateType'
    };
    this.sortField = sortMap[event.field] || 'CreatedOn';
    this.sortOrder = event.order === 1 ? 'ASC' : 'DESC';
    this.applyTemplateFamilies();
  }

  /**
   * Handle filter change
   */
  onFilterChange(): void {
    this.filterChangeSubject.next({
      name: this.nameFilter ?? '',
      type: this.typeFilter ?? null,
      status: this.statusFilter ?? null,
      department: this.departmentFilter ?? ''
    });
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.nameFilter = '';
    this.typeFilter = null;
    this.statusFilter = null;
    this.departmentFilter = '';
    this.onFilterChange();
  }
  //#endregion

  //#region Actions
  /**
   * View template details
   */
  viewTemplate(templateId: number): void {
    this.router.navigate(['./templates/view', templateId], {
      relativeTo: this.route
    });
  }

  /**
   * Edit template
   */
  editTemplate(templateId: number): void {
    this.router.navigate(['./template/edit', templateId], {
      relativeTo: this.route
    });
  }

  /**
   * Create new template
   */
  createTemplate(): void {
    this.router.navigate(['./template/new'], { relativeTo: this.route });
  }

  /**
   * Toggle template active status
   */
  toggleTemplateStatus(template: AppraisalTemplateListItemDto): void {
    if (!this.canInteractWithTemplate(template)) {
      return;
    }

    const newStatus = !template.IsActive;
    const message = newStatus
      ? this.translate.instant('CONFIRM_ACTIVATE_TEMPLATE') ||
        'Are you sure you want to activate this form?'
      : this.translate.instant('CONFIRM_DEACTIVATE_TEMPLATE') ||
        'Are you sure you want to deactivate this form?';

    this.confirmationService.confirm({
      message: message,
      accept: () => {
        this.setTemplateActionState(template.Id, 'toggling');
        this.subscriptions.push(
          this.templateService.setActiveStatus(template.Id).subscribe({
            next: () => {
              const successMsg = newStatus
                ? this.translate.instant('TEMPLATE_ACTIVATED_SUCCESS')
                : this.translate.instant('TEMPLATE_DEACTIVATED_SUCCESS');
              this.showSuccess(successMsg);
              this.loadTemplates();
            },
            error: error => {
              this.clearTemplateActionState(template.Id);
              const errorMsg = newStatus
                ? this.translate.instant('TEMPLATE_ACTIVATED_ERROR')
                : this.translate.instant('TEMPLATE_DEACTIVATED_ERROR');
              this.showError(error.message || errorMsg);
            }
          })
        );
      }
    });
  }

  /**
   * Open change statuses dialog
   */
  openUpdateStatusesDialog(): void {
    if (
      this.selectedTemplates.length === 0 ||
      this.isUpdatingSelectedTemplateStatuses ||
      this.isDeletingSelectedTemplates ||
      this.hasPendingTemplateActions()
    ) {
      return;
    }

    this.openChangeStatusDialog = true;
  }

  /**
   * Change status of selected templates
   * @param isActive New active status
   */
  changeStatusOfSelectedTemplates(isActive: boolean): void {
    if (
      this.selectedTemplates.length === 0 ||
      this.isUpdatingSelectedTemplateStatuses ||
      this.isDeletingSelectedTemplates ||
      this.hasPendingTemplateActions()
    ) {
      return;
    }

    this.isUpdatingSelectedTemplateStatuses = true;
    this.selectedTemplateStatusTarget = isActive;
    const updateRequest: AppraisalTemplateBatchStatusDto = {
      TemplateIds: this.selectedTemplates.map(t => t.CurrentTemplate.Id),
      IsActive: isActive
    };

    this.subscriptions.push(
      this.templateService.batchUpdateStatus(updateRequest).subscribe({
        next: () => {
          const successMsg = isActive
            ? this.translate.instant('TEMPLATES_ACTIVATED_SUCCESS')
            : this.translate.instant('TEMPLATES_DEACTIVATED_SUCCESS');
          this.showSuccess(successMsg);
          this.selectedTemplates = [];
          this.openChangeStatusDialog = false;
          this.loadTemplates();
        },
        error: error => {
          this.isUpdatingSelectedTemplateStatuses = false;
          this.selectedTemplateStatusTarget = null;
          const errorMsg = isActive
            ? this.translate.instant('TEMPLATES_ACTIVATED_ERROR')
            : this.translate.instant('TEMPLATES_DEACTIVATED_ERROR');
          this.showError(error.message || errorMsg);
        }
      })
    );
  }

  /**
   * Delete template
   */
  deleteTemplate(template: AppraisalTemplateListItemDto): void {
    if (!this.canInteractWithTemplate(template)) {
      return;
    }

    if (!this.canDeleteTemplateRow(template)) {
      this.showError(this.translate.instant('TEMPLATE_DELETE_BLOCKED_HAS_MEETINGS'));
      return;
    }

    this.confirmationService.confirm({
      message:
        this.translate.instant('CONFIRM_DELETE_TEMPLATE', {
          name: template.Name
        }) || `Are you sure you want to delete form "${template.Name}"?`,
      accept: () => {
        this.setTemplateActionState(template.Id, 'deleting');
        this.subscriptions.push(
          this.templateService.delete(template.Id).subscribe({
            next: () => {
              this.showSuccess(
                this.translate.instant('TEMPLATE_DELETED_SUCCESS')
              );
              this.loadTemplates();
            },
            error: error => {
              this.clearTemplateActionState(template.Id);
              this.showError(this.getTemplateDeleteErrorMessage(error));
            }
          })
        );
      }
    });
  }

  /**
   * Delete selected templates
   */
  deleteSelectedTemplates(): void {
    if (
      this.selectedTemplates.length === 0 ||
      this.isDeletingSelectedTemplates ||
      this.isUpdatingSelectedTemplateStatuses ||
      this.hasPendingTemplateActions()
    ) {
      return;
    }

    if (this.hasSelectedTemplatesUsedByMeetings()) {
      this.showError(this.translate.instant('TEMPLATES_DELETE_BLOCKED_HAS_MEETINGS'));
      return;
    }

    this.confirmationService.confirm({
      message:
        this.translate.instant('CONFIRM_DELETE_TEMPLATES', {
          count: this.selectedTemplates.length
        }) ||
        `Are you sure you want to delete ${this.selectedTemplates.length} forms?`,
      accept: () => {
        this.isDeletingSelectedTemplates = true;
        const selectedTemplates = [...this.selectedTemplates];
        const ids = selectedTemplates.map(t => t.CurrentTemplate.Id);
        const deleteRequests = ids.map(id =>
          this.templateService.delete(id).pipe(
            map(() => ({ success: true as const, id })),
            catchError(error => {
              console.error(`Failed to delete form ${id}:`, error);
              return of({
                success: false as const,
                id,
                message: this.getTemplateDeleteErrorMessage(error)
              });
            })
          )
        );

        this.isLoading = true;
        this.subscriptions.push(
          forkJoin(deleteRequests).subscribe({
            next: results => {
              const failedDeletions = results.filter(
                (
                  result
                ): result is {
                  success: false;
                  id: number;
                  message: string;
                } => !result.success
              );
              const deletedCount = selectedTemplates.length - failedDeletions.length;

              this.selectedTemplates = [];

              if (failedDeletions.length > 0) {
                const firstFailureMessage =
                  failedDeletions[0].message ||
                  this.translate.instant('TEMPLATES_DELETED_ERROR');
                const detailMessage =
                  failedDeletions.length === 1
                    ? firstFailureMessage
                    : `${failedDeletions.length} forms failed to delete. ${firstFailureMessage}`;
                this.showError(detailMessage);
              } else {
                this.showSuccess(
                  this.translate.instant('TEMPLATES_DELETED_SUCCESS', {
                    count: deletedCount
                  })
                );
              }

              this.loadTemplates();
            },
            error: error => {
              console.error('Error deleting forms:', error);
              this.isLoading = false;
              this.isDeletingSelectedTemplates = false;
              this.showError(
                error.message ||
                  this.translate.instant('TEMPLATES_DELETED_ERROR')
              );
            }
          })
        );
      }
    });
  }

  /**
   * Export template
   */
  exportTemplate(template: any): void {
    // Using mock data - simulate export
    // TODO: Uncomment when backend is ready
    /*
    this.subscriptions.push(
      this.templateService.exportTemplate(template.id).subscribe({
        next: (data) => {
          this.downloadJSON(data, `${template.name}.json`);
          this.showSuccess(this.translate.instant('TEMPLATE_EXPORTED_SUCCESS'));
        },
        error: (error) => {
          console.error('Error exporting form:', error);
          this.showError(error.message || this.translate.instant('TEMPLATE_EXPORTED_ERROR'));
        }
      })
    );
    */

    // Mock implementation - export template as JSON
    this.downloadJSON(template, `${template.Name}.json`);
    this.showSuccess(
      this.translate.instant('TEMPLATE_EXPORTED_SUCCESS') ||
        'Form exported successfully'
    );
  }

  /**
   * Get template type display text
   */
  getTemplateTypeText(type: AppraisalTemplateType): string {
    const typeMap: { [key: number]: string } = {
      [AppraisalTemplateType.Standard]: 'STANDARD',
      [AppraisalTemplateType.Leader]: 'LEADER',
      [AppraisalTemplateType.Project]: 'PROJECT'
    };
    return typeMap[type] || 'UNKNOWN';
  }

  /**
   * Get template type badge class
   */
  getTemplateTypeClass(type: AppraisalTemplateType): string {
    const typeMap: { [key: number]: string } = {
      [AppraisalTemplateType.Standard]: 'badge-primary',
      [AppraisalTemplateType.Leader]: 'badge-warning',
      [AppraisalTemplateType.Project]: 'badge-info'
    };
    return typeMap[type] || 'badge-secondary';
  }

  /**
   * Download JSON file
   */
  private downloadJSON(data: any, filename: string): void {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Show success message
   */
  showSuccess(message: string): void {
    this.notifications.add({ severity: 'success', summary: this.translate.instant('SUCCESS') || 'Success', detail: message || this.translate.instant('ADD_UPDATE_SUCCESS') || 'Operation completed successfully.' });
  }

  /**
   * Show error message
   */
  showError(message: string): void {
    this.notifications.add({ severity: 'error', summary: this.translate.instant('ERROR') || 'Error', detail: message || this.translate.instant('ERROR') || 'Operation failed.' });
  }

  /**
   * Check if template name is unique
   */
  checkTemplateNameAvailability(name: string, excludeId?: number): void {
    this.subscriptions.push(
      this.templateService
        .checkNameAvailability(BaseServices.BusinessId, name, 1, excludeId)
        .subscribe({
          next: isAvailable => {
            if (!isAvailable) {
              this.showError(this.translate.instant('TEMPLATE_NAME_EXISTS'));
            }
          },
          error: error => {
            console.error('Error checking form name:', error);
          }
        })
    );
  }

  hasVersionHistory(family: TemplateFamilyRow): boolean {
    return family.Versions.length > 1;
  }

  getOlderVersionCount(family: TemplateFamilyRow): number {
    return Math.max(family.Versions.length - 1, 0);
  }

  getHistoricalVersions(family: TemplateFamilyRow): AppraisalTemplateListItemDto[] {
    return family.Versions.filter(
      version => version.Id !== family.CurrentTemplate.Id
    );
  }

  getVersionStateLabel(
    family: TemplateFamilyRow,
    version: AppraisalTemplateListItemDto
  ): string {
    if (version.Id === family.CurrentTemplate.Id) {
      return ( this.translate.instant('APPRAISAL_CURRENT_VERSION_SHORT') || 'Current'
      );
    }

    return this.translate.instant('HISTORY') || 'History';
  }

  openActionMenu(
    event: MouseEvent,
    template: AppraisalTemplateListItemDto
  ): void {
    if (!this.canInteractWithTemplate(template)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const trigger = event.currentTarget as HTMLElement | null;
    if (!trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const menuWidth = 190;
    const menuHeight = 220;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    this.currentActionTemplate = template;
    this.actionMenuPosition = {
      top: Math.min(rect.bottom + 6, viewportHeight - menuHeight - 8),
      left: Math.max(8, Math.min(rect.right - menuWidth, viewportWidth - menuWidth - 8))
    };
  }

  closeActionMenu(): void {
    this.currentActionTemplate = null;
  }

  onTemplateAction(
    action: 'view' | 'edit' | 'toggle' | 'delete',
    template: AppraisalTemplateListItemDto
  ): void {
    this.closeActionMenu();

    switch (action) {
      case 'view':
        this.viewTemplate(template.Id);
        break;
      case 'edit':
        this.editTemplate(template.Id);
        break;
      case 'toggle':
        this.toggleTemplateStatus(template);
        break;
      case 'delete':
        this.deleteTemplate(template);
        break;
      default:
        break;
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeActionMenu();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.closeActionMenu();
  }

  private applyTemplateFamilies(): void {
    const filteredFamilies = this.filterTemplateFamilies(
      this.buildTemplateFamilies(this.allTemplateVersions)
    );
    const sortedFamilies = this.sortTemplateFamilies(filteredFamilies);
    this.templateFamilies = sortedFamilies;
    this.totalItems = sortedFamilies.length;
    this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 0;

    const maxFirst = Math.max(this.totalItems - this.pageSize, 0);
    if (this.first > maxFirst) {
      this.first = 0;
      this.currentPage = 1;
    }

    this.selectedTemplates = this.selectedTemplates.filter(selected =>
      sortedFamilies.some(family => family.FamilyKey === selected.FamilyKey)
    );
  }

  canInteractWithTemplate(template: AppraisalTemplateListItemDto | null | undefined): boolean {
    return ( !!template &&
      !this.isDeletingSelectedTemplates &&
      !this.isUpdatingSelectedTemplateStatuses &&
      !this.isTemplateActionPending(template.Id));
  }

  canDeleteTemplateRow(template: AppraisalTemplateListItemDto | null | undefined): boolean {
    return ( this.canDeleteTemplate &&
      this.canInteractWithTemplate(template) &&
      !template?.HasMeetings
    );
  }

  hasSelectedTemplatesUsedByMeetings(): boolean {
    return this.selectedTemplates.some(family => family.CurrentTemplate.HasMeetings);
  }

  private getTemplateDeleteErrorMessage(error: any): string {
    const message = error?.message || '';
    const normalizedMessage = message.toLowerCase();

    if (
      normalizedMessage.includes('existing dialog meeting') ||
      normalizedMessage.includes('cannot be deleted')
    ) {
      return this.translate.instant('TEMPLATE_DELETE_BLOCKED_HAS_MEETINGS');
    }

    return message || this.translate.instant('TEMPLATE_DELETED_ERROR');
  }

  isTemplateActionPending(templateId: number): boolean {
    return !!this.templateActionStates[templateId];
  }

  hasPendingTemplateActions(): boolean {
    return Object.keys(this.templateActionStates).length > 0;
  }

  private setTemplateActionState(
    templateId: number,
    state: 'toggling' | 'deleting'
  ): void {
    this.templateActionStates = {
      ...this.templateActionStates,
      [templateId]: state
    };
  }

  private clearTemplateActionState(templateId: number): void {
    const { [templateId]: _removed, ...rest } = this.templateActionStates;
    this.templateActionStates = rest;
  }

  private resetTemplateActionState(): void {
    this.templateActionStates = {};
    this.isDeletingSelectedTemplates = false;
    this.isUpdatingSelectedTemplateStatuses = false;
    this.selectedTemplateStatusTarget = null;
  }

  private buildTemplateFamilies(
    templates: AppraisalTemplateListItemDto[]
  ): TemplateFamilyRow[] {
    const familyMap = new Map<string, AppraisalTemplateListItemDto[]>();

    templates.forEach(template => {
      const familyKey = this.getTemplateFamilyKey(template);
      const familyItems = familyMap.get(familyKey) || [];
      familyItems.push(template);
      familyMap.set(familyKey, familyItems);
    });

    return Array.from(familyMap.entries()).map(([familyKey, versions]) => {
      const sortedVersions = [...versions].sort((a, b) =>
        this.compareTemplatesByVersion(a, b)
      );
      const currentTemplate =
        sortedVersions.find(version => version.IsActive) || sortedVersions[0];

      return {
        FamilyKey: familyKey,
        DisplayName: currentTemplate.Name,
        CurrentTemplate: currentTemplate,
        Versions: sortedVersions
      };
    });
  }

  private filterTemplateFamilies(
    families: TemplateFamilyRow[]
  ): TemplateFamilyRow[] {
    const normalizedName = this.nameFilter.trim().toLowerCase();
    const normalizedDepartment = this.departmentFilter.trim().toLowerCase();

    return families.filter(family => {
      const current = family.CurrentTemplate;
      const matchesName =
        !normalizedName ||
        family.Versions.some(version =>
          version.Name?.toLowerCase().includes(normalizedName)
        );
      const matchesType =
        this.typeFilter === null ||
        this.typeFilter === undefined ||
        current.TemplateType === this.typeFilter;
      const matchesStatus =
        this.statusFilter === null ||
        this.statusFilter === undefined ||
        current.IsActive === this.statusFilter;
      const matchesDepartment =
        !normalizedDepartment ||
        current.AssignedDepartments.some(department =>
          department.Name?.toLowerCase().includes(normalizedDepartment)
        );

      return matchesName && matchesType && matchesStatus && matchesDepartment;
    });
  }

  private sortTemplateFamilies(
    families: TemplateFamilyRow[]
  ): TemplateFamilyRow[] {
    const direction = this.sortOrder === 'ASC' ? 1 : -1;

    return [...families].sort((left, right) => {
      let comparison = 0;

      switch (this.sortField) {
        case 'TemplateType':
          comparison =
            Number(left.CurrentTemplate.TemplateType) -
            Number(right.CurrentTemplate.TemplateType);
          break;
        case 'CreatedOn':
          comparison =
            new Date(left.CurrentTemplate.CreatedBy?.CreatedOn || 0).getTime() -
            new Date(right.CurrentTemplate.CreatedBy?.CreatedOn || 0).getTime();
          break;
        case 'Name':
        default:
          comparison = left.DisplayName.localeCompare(
            right.DisplayName,
            undefined,
            { sensitivity: 'base' }
          );
          break;
      }

      if (comparison === 0) {
        comparison =
          this.compareTemplatesByVersion(
            left.CurrentTemplate,
            right.CurrentTemplate
          ) * -1;
      }

      return comparison * direction;
    });
  }

  private compareTemplatesByVersion(
    left: AppraisalTemplateListItemDto,
    right: AppraisalTemplateListItemDto
  ): number {
    if (left.Version !== right.Version) {
      return right.Version - left.Version;
    }

    const leftCreated = new Date(left.CreatedBy?.CreatedOn || 0).getTime();
    const rightCreated = new Date(right.CreatedBy?.CreatedOn || 0).getTime();
    return rightCreated - leftCreated;
  }

  private normalizeFamilyKey(name: string, fallbackId: number): string {
    const normalized = (name || '').trim().toLowerCase();
    return normalized || `template-family-${fallbackId}`;
  }

  private getTemplateFamilyKey(
    template: AppraisalTemplateListItemDto
  ): string {
    return (
      template.TemplateFamilyId ||
      this.normalizeFamilyKey(template.Name, template.Id)
    );
  }

  //#endregion
}
